import React, { useState } from 'react';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../services/axiosInstance';
import AuthShell from './auth/AuthShell';
import { underlineStyle, primaryButtonSx, errorBoxSx } from './auth/authStyles';

export default function SetupAccountPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async () => {
        setError(null);
        if (!token) { setError('This setup link is invalid or missing a token.'); return; }
        if (!password) { setError('Please enter a password.'); return; }
        if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
        if (password !== confirmPassword) { setError('Passwords do not match.'); return; }

        setLoading(true);
        try {
            await axiosInstance.post('/api/auth/employee-setup', { token, password });
            setSuccess(true);
        } catch (err) {
            const msg = err?.response?.data?.message;
            setError(msg || 'This setup link is invalid or has expired. Please contact your administrator for a new invite.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthShell centered>
            {success ? (
                <Box sx={{ textAlign: 'center' }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 56, color: '#22c55e', mb: 2 }} />
                    <Typography variant="h6" fontWeight={700} sx={{ color: '#1a2332', mb: 1 }}>Account set up!</Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
                        Your password has been created. You can now sign in.
                    </Typography>
                    <Button fullWidth variant="contained" onClick={() => navigate('/login')} sx={primaryButtonSx}>
                        Go to login
                    </Button>
                </Box>
            ) : (
                <>
                    <Box sx={{ mb: 4, textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ display: 'block', color: '#427898', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, mb: 1, fontSize: 11 }}>
                            Plankton CRM
                        </Typography>
                        <Typography variant="h5" fontWeight={700} sx={{ color: '#1a2332', mb: 0.5 }}>Set up your account</Typography>
                        <Typography variant="body2" sx={{ color: '#7f8c9a' }}>Choose a password to activate your access.</Typography>
                    </Box>

                    {error && (
                        <Box sx={errorBoxSx}>
                            <Typography variant="body2" color="error">{error}</Typography>
                        </Box>
                    )}

                    <TextField fullWidth variant="standard" type="password" label="Password"
                        value={password} onChange={e => { setPassword(e.target.value); if (error) setError(null); }}
                        onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                        sx={{ mb: 3, ...underlineStyle }} />

                    <TextField fullWidth variant="standard" type="password" label="Confirm password"
                        value={confirmPassword} onChange={e => { setConfirmPassword(e.target.value); if (error) setError(null); }}
                        onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                        sx={{ mb: 4, ...underlineStyle }} />

                    <Button fullWidth variant="contained" disabled={loading} onClick={handleSubmit} sx={primaryButtonSx}>
                        {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Set up account'}
                    </Button>
                </>
            )}
        </AuthShell>
    );
}
